import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import SplitPane from "./components/SplitPane";
import StatsCharts from "./components/StatsCharts";
import StatsTable from "./components/StatsTable";
import PipelineDetail from "./components/PipelineDetail";
import PerformanceTab from "./components/PerformanceTab";
import CostUsageTab from "./components/CostUsageTab";
import FalconPayloadTab from "./components/FalconPayloadTab";
import LoginPage from "./components/LoginPage";
import SettingsModal from "./components/SettingsModal";
import EventLogs from "./components/EventLogs";
import "./App.css";

const API = process.env.REACT_APP_API_URL || "";

// Redirect to login on 401 (session expired)
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      window.location.href = `${API}/auth/login`;
    }
    return Promise.reject(err);
  }
);

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("opspulse-theme") || "light");
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [messageIds, setMessageIds] = useState([]);
  const [searchId, setSearchId] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [stats, setStats] = useState(null);
  const [recentStats, setRecentStats] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const skipRef = useRef(0);
  const sentinelRef = useRef(null);
  const LIMIT = 50;
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [detailTab, setDetailTab] = useState("falcon");

  // Check authentication on mount
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        if (data.authenticated) setUser(data.user);
        setAuthChecked(true);
      })
      .catch(() => setAuthChecked(true));
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/runs`, { withCredentials: true });
      setMessageIds(data);
    } catch {}
  }, []);

  const loadRecent = useCallback(async (reset = true) => {
    if (reset) {
      skipRef.current = 0;
      setRecentStats([]);
    }
    const isInitial = reset;
    if (isInitial) setLoading(true);
    else setLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/api/stats?limit=${LIMIT}&skip=${skipRef.current}`, { withCredentials: true });
      setRecentStats((prev) => reset ? data.results : [...prev, ...data.results]);
      setTotal(data.total);
      skipRef.current += data.results.length;
      setHasMore(skipRef.current < data.total);
    } catch {}
    finally {
      if (isInitial) setLoading(false);
      else setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadRuns();
      loadRecent();
    }
  }, [user, loadRuns, loadRecent]);

  const fetchStats = async (mid) => {
    if (!mid) return;
    setLoading(true);
    setError("");
    try {
      const [statsRes, metricsRes] = await Promise.allSettled([
        axios.get(`${API}/api/stats/${encodeURIComponent(mid)}`, { withCredentials: true }),
        axios.get(`${API}/api/metrics/${encodeURIComponent(mid)}`, { withCredentials: true }),
      ]);
      if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      else setError(statsRes.reason?.response?.data?.error || "Failed to fetch stats");
      setMetrics(metricsRes.status === "fulfilled" ? metricsRes.value.data : null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = searchId.trim();
    if (!q) return;
    setSelectedId("");
    setStats(null);
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/api/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setSearchResults(data);
    } catch (err) {
      setError(err.response?.data?.error || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchId("");
    setSearchResults(null);
    setError("");
  };

  const handleDropdown = (e) => {
    const val = e.target.value;
    setSelectedId(val);
    setSearchId("");
    fetchStats(val);
  };

  const handleSelect = (mid) => {
    setSelectedId(mid);
    fetchStats(mid);
  };

  const closeDetail = () => {
    setStats(null);
    setMetrics(null);
    setSelectedId("");
    setError("");
  };

  const [refreshing, setRefreshing] = useState(false);
  const [chartsRefreshKey, setChartsRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setChartsRefreshKey((k) => k + 1);
    await Promise.all([loadRuns(), loadRecent(true)]);
    setRefreshing(false);
  }, [loadRuns, loadRecent]);

  // Infinite scroll — observe sentinel div at bottom of the list
  // Also re-attach when detail pane opens/closes (stats) so pane resize re-triggers intersection
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadRecent(false);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadRecent, stats]);

  const [activeTab, setActiveTab] = useState("clearance");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("opspulse-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const handleLogout = () => {
    window.location.href = `${API}/auth/logout`;
  };

  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showEventLogs, setShowEventLogs] = useState(false);
  const profileRef = React.useRef(null);

  useEffect(() => {
    if (!showProfile) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfile(false);
      }
    };
    // Use capture phase so outside click closes menu before any bubbling
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showProfile]);

  // Show nothing while checking auth
  if (!authChecked) {
    return <div className="auth-loading">Loading...</div>;
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  const leftPane = <StatsCharts refreshKey={chartsRefreshKey} />;

  const middlePane = (
    <div className="middle-content">
      <div className="controls">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by Message ID or Mail Subject..."
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
          />
          <button type="submit" disabled={!searchId.trim()}>Search</button>
          {searchResults && <button type="button" onClick={clearSearch} className="clear-btn">Clear</button>}
        </form>

        <div className="dropdown-group">
          <label>Or select:</label>
          <select value={selectedId} onChange={handleDropdown}>
            <option value="">-- Select --</option>
            {messageIds.map((run) => (
              <option key={run.message_id} value={run.message_id}>{run.subject}</option>
            ))}
          </select>
        </div>
      </div>

      {searchResults ? (
        <>
          <h2>Search Results ({searchResults.length} found)</h2>
          <StatsTable data={searchResults} onSelect={handleSelect} activeId={selectedId} />
        </>
      ) : (
        <>
          <h2 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            Recent Processing Results ({recentStats.length} / {total})
            <button className={`refresh-inline-btn${refreshing ? " refreshing" : ""}`} onClick={handleRefresh} disabled={refreshing} title="Refresh">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={refreshing ? { animation: "spin 0.8s linear infinite" } : {}}><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
            </button>
          </h2>
          <StatsTable
            data={recentStats}
            onSelect={handleSelect}
            activeId={selectedId}
            sentinelRef={sentinelRef}
            hasMore={hasMore}
            loadingMore={loadingMore}
          />
        </>
      )}
    </div>
  );

  const rightPane = stats ? (
    <div className="detail-content">
      <div className="detail-header">
        <div className="detail-tabs">
          <button
            className={`detail-tab ${detailTab === "falcon" ? "detail-tab-active" : ""}`}
            onClick={() => setDetailTab("falcon")}
          ><span className="tab-icon">📝</span> Submission Info</button>
          <button
            className={`detail-tab ${detailTab === "performance" ? "detail-tab-active" : ""}`}
            onClick={() => setDetailTab("performance")}
          ><span className="tab-icon">⚡</span> Performance</button>
          <button
            className={`detail-tab ${detailTab === "cost" ? "detail-tab-active" : ""}`}
            onClick={() => setDetailTab("cost")}
          ><span className="tab-icon">💰</span> Cost & Usage</button>
          <button
            className={`detail-tab ${detailTab === "collections" ? "detail-tab-active" : ""}`}
            onClick={() => setDetailTab("collections")}
          ><span className="tab-icon">📋</span> Collections</button>
        </div>
        <button className="close-detail-btn" onClick={closeDetail} title="Close">✕</button>
      </div>
      {loading && <p className="info">Loading...</p>}
      {error && <p className="error">{error}</p>}
      {detailTab === "performance" && <PerformanceTab metrics={metrics} />}
      {detailTab === "cost" && <CostUsageTab metrics={metrics} />}
      {detailTab === "collections" && <PipelineDetail stats={stats} />}
      {detailTab === "falcon" && <FalconPayloadTab messageId={selectedId} />}
    </div>
  ) : null;

  return (
    <div className="app">
      <header>
        <h1>🔍 Ops Pulse - Pipeline Monitoring Tool</h1>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div className="profile-dropdown" ref={profileRef}>
            <button className="profile-btn" onMouseDown={(e) => { e.stopPropagation(); setShowProfile(v => !v); }}>
              <span>{user.name || user.email}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {showProfile && (
              <div className="profile-menu">
                <button onMouseDown={(e) => { e.stopPropagation(); setShowEventLogs(true); setShowProfile(false); }}>Event Logs</button>
                <button onMouseDown={(e) => { e.stopPropagation(); setShowSettings(true); setShowProfile(false); }}>Settings</button>
                <button onMouseDown={(e) => { e.stopPropagation(); toggleTheme(); setShowProfile(false); }}>{theme === "dark" ? "Light Mode" : "Dark Mode"}</button>
                <div className="profile-divider" />
                <button onMouseDown={(e) => { e.stopPropagation(); handleLogout(); }} className="profile-logout">Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="tab-bar">
        <button
          className={`tab ${activeTab === "clearance" ? "tab-active" : ""}`}
          onClick={() => setActiveTab("clearance")}
        >
          Clearance
        </button>
      </div>
      <div className="tab-content">
        {activeTab === "clearance" && (
          <SplitPane
            left={leftPane}
            middle={middlePane}
            right={rightPane}
            defaultLeftWidth={15}
            defaultRightWidth={stats ? 35 : 0}
            minRight={0}
            maxRight={55}
          />
        )}
      </div>
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showEventLogs && (
        <div className="el-fullscreen-overlay">
          <EventLogs onBack={() => setShowEventLogs(false)} />
        </div>
      )}
    </div>
  );
}

export default App;
